import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "balance",
    "detectorLabel",
    "emptyPreview",
    "faceCount",
    "faceOptions",
    "facesList",
    "fileInput",
    "message",
    "overlay",
    "previewImage",
    "previewShell",
    "runButton",
    "selectionSummary"
  ]

  static values = {
    bootstrapUrl: String,
    copy: Object,
    createUrl: String,
    demo: Boolean,
    detectUrl: String,
    taskToken: String
  }

  connect() {
    this.faces = []
    this.detectedFaces = []
    this.activeTask = null
    this.selectedFaceId = null
    this.selectionMode = "auto"
    this.targetFaceIndex = null
    this.targetFile = null
    this.targetUploadToken = null
    this.previewDimensions = null
    this.previewObjectUrl = null
    this.detectionRequestId = 0
    this.queued = false
    this.initData = this.readInitData()
    this.taskToken = this.taskTokenValue || this.readTaskToken()
    this.renderFaceBoxesOnResize = () => this.renderFaceBoxes()
    window.addEventListener("resize", this.renderFaceBoxesOnResize)

    try { window.Telegram?.WebApp?.ready() } catch (_) {}

    if (this.demoValue && !this.initData) {
      this.loadDemoState()
      return
    }

    if (!this.initData) {
      this.renderAuthRequired()
      return
    }

    this.bootstrap()
  }

  disconnect() {
    window.removeEventListener("resize", this.renderFaceBoxesOnResize)
    if (this.previewObjectUrl) URL.revokeObjectURL(this.previewObjectUrl)
  }

  readInitData() {
    const fromTelegram = window.Telegram?.WebApp?.initData || ""
    if (fromTelegram) return fromTelegram

    const params = new URLSearchParams(window.location.search)
    return params.get("init_data") || ""
  }

  readTaskToken() {
    const params = new URLSearchParams(window.location.search)
    return params.get("task_token") || ""
  }

  bootstrap() {
    const body = new FormData()
    body.append("init_data", this.initData)
    if (this.taskToken) body.append("task_token", this.taskToken)

    fetch(this.bootstrapUrlValue, { method: "POST", body })
      .then((response) => response.json().then((json) => ({ ok: response.ok, json })))
      .then(({ ok, json }) => {
        if (!ok || json.status !== "ok") {
          const message = json.message || this.t("base_load_failed", "Could not load your base face.")
          this.renderBaseFaceUnavailable(message)
          this.showMessage(message, "error")
          return
        }

        this.activeTask = json.active_task || null
        this.faces = json.faces || []
        this.selectedFaceId = json.default_face_id || this.faces[0]?.public_id || null
        this.balanceTarget.textContent = this.formatBalance(json.balance)
        this.renderFaces()
      })
      .catch(() => {
        const message = this.t(
          "base_load_failed_reopen",
          "Could not load your base face. Please close this and open the picker again from Telegram."
        )
        this.renderBaseFaceUnavailable(message)
        this.showMessage(message, "error")
      })
  }

  loadDemoState() {
    this.balanceTarget.textContent = "$12.40"
    this.faces = [
      { public_id: "demo-face-1", name: this.t("demo_default_face", "Default face"), is_default: true, photo_url: "" },
      { public_id: "demo-face-2", name: this.t("demo_second_face", "Second face"), is_default: false, photo_url: "" }
    ]
    this.selectedFaceId = "demo-face-1"
    this.renderFaces()
    this.showMessage(
      this.t("demo_loaded", "Demo mode is showing the picker UI without queueing a real task."),
      "info"
    )
  }

  renderAuthRequired() {
    this.facesListTarget.innerHTML = ""
    this.faceCountTarget.textContent = ""
    this.runButtonTarget.disabled = true
    this.showMessage(this.t("auth_required", "Open this from Telegram after sending your base photo."), "error")
  }

  renderBaseFaceUnavailable(message = this.t("base_load_failed", "Could not load your base face.")) {
    this.facesListTarget.innerHTML = ""
    this.faceCountTarget.textContent = ""
    this.runButtonTarget.disabled = true

    const card = document.createElement("div")
    card.className = "rounded-md border border-red-500/30 bg-red-950/40 p-3 text-sm text-red-100"
    card.textContent = message
    this.facesListTarget.appendChild(card)
  }

  renderFaces() {
    this.facesListTarget.innerHTML = ""
    if (this.activeTask) {
      this.faceCountTarget.textContent = this.t("from_chat", "from chat")
      this.facesListTarget.appendChild(this.activeTaskBaseCard())
      this.updateRunState()
      return
    }

    this.faceCountTarget.textContent = this.tp("saved_count", this.faces.length, "%{count} saved", "%{count} saved")

    if (this.faces.length === 0) {
      this.facesListTarget.innerHTML = ""
      const empty = document.createElement("div")
      empty.className = "rounded-md border border-gray-800 bg-gray-900 p-3 text-sm text-gray-300"
      empty.textContent = this.t("add_saved_face_first", "Add a saved face first.")
      this.facesListTarget.appendChild(empty)
      this.runButtonTarget.disabled = true
      return
    }

    this.faces.forEach((face) => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = this.faceButtonClasses(face.public_id === this.selectedFaceId)
      button.dataset.faceId = face.public_id
      button.addEventListener("click", () => {
        this.selectedFaceId = face.public_id
        this.renderFaces()
        this.updateRunState()
      })

      button.appendChild(this.facePreviewElement(face))

      const name = document.createElement("span")
      name.className = "mt-2 block truncate text-sm font-medium"
      name.textContent = face.name || this.t("saved_face", "Saved face")
      button.appendChild(name)

      const label = document.createElement("span")
      label.className = "mt-1 block text-xs text-gray-400"
      label.textContent = face.is_default ? this.t("default_badge", "Default") : this.t("saved_badge", "Saved")
      button.appendChild(label)

      this.facesListTarget.appendChild(button)
    })
  }

  activeTaskBaseCard() {
    const wrapper = document.createElement("div")
    wrapper.className = "w-32 rounded-md border border-blue-500/40 bg-blue-950/40 p-2 text-sm text-blue-100"

    const preview = document.createElement("div")
    preview.className = "flex h-24 w-full items-center justify-center overflow-hidden rounded bg-gray-900 text-center text-xs text-blue-100/80"

    const imageUrl = this.activeTask?.base_image_preview_url
    if (imageUrl) {
      const image = document.createElement("img")
      image.src = imageUrl
      image.alt = this.t("base_face_alt", "Base face")
      image.className = "h-full w-full object-cover"
      image.addEventListener("error", () => {
        preview.innerHTML = ""
        preview.textContent = this.t("base_photo", "Base photo")
      })
      preview.appendChild(image)
    } else {
      preview.textContent = this.t("base_photo", "Base photo")
    }

    const label = document.createElement("span")
    label.className = "mt-2 block text-sm font-medium"
    label.textContent = this.t("base_photo", "Base photo")

    const hint = document.createElement("span")
    hint.className = "mt-1 block text-xs text-blue-100/70"
    hint.textContent = this.t("selected_in_telegram", "Selected in Telegram")

    wrapper.appendChild(preview)
    wrapper.appendChild(label)
    wrapper.appendChild(hint)
    return wrapper
  }

  facePreviewElement(face) {
    const wrapper = document.createElement("div")
    wrapper.className = "flex h-16 w-full items-center justify-center overflow-hidden rounded bg-gray-800 text-xs text-gray-400"

    if (!face.photo_url) {
      wrapper.textContent = this.t("no_image", "No image")
      return wrapper
    }

    const image = document.createElement("img")
    image.src = face.photo_url
    image.alt = ""
    image.className = "h-full w-full object-cover"
    image.addEventListener("error", () => {
      wrapper.textContent = this.t("no_image", "No image")
    })
    wrapper.appendChild(image)
    return wrapper
  }

  faceButtonClasses(selected) {
    const base = "rounded-md border p-2 text-left transition"
    return selected
      ? `${base} border-blue-400 bg-blue-950/70`
      : `${base} border-gray-800 bg-gray-900 hover:border-blue-400`
  }

  targetChanged() {
    const file = this.fileInputTarget.files?.[0]
    if (!file) return

    this.targetFile = file
    this.targetUploadToken = null
    this.detectedFaces = []
    this.detectionRequestId += 1
    this.selectionMode = "auto"
    this.targetFaceIndex = null
    this.detectorLabelTarget.textContent = this.t("detecting", "Detecting...")
    this.faceOptionsTarget.innerHTML = ""
    this.overlayTarget.innerHTML = ""
    this.showMessage("", "clear")

    if (this.previewObjectUrl) URL.revokeObjectURL(this.previewObjectUrl)
    this.previewObjectUrl = URL.createObjectURL(file)
    this.emptyPreviewTarget.classList.add("hidden")
    this.previewImageTarget.classList.remove("hidden")
    this.previewImageTarget.src = this.previewObjectUrl
    this.updateRunState()
  }

  previewLoaded() {
    this.previewDimensions = {
      width: this.previewImageTarget.naturalWidth,
      height: this.previewImageTarget.naturalHeight
    }
    this.detectFaces()
  }

  detectFaces() {
    if (this.demoValue && !this.initData) {
      this.applyDetectedFaces(this.localPreviewFaces(), "demo")
      return
    }

    if (!this.initData || !this.targetFile) return

    const requestId = ++this.detectionRequestId
    const targetFile = this.targetFile
    const body = new FormData()
    body.append("init_data", this.initData)
    body.append("target_file", targetFile)
    if (this.taskToken) body.append("task_token", this.taskToken)

    fetch(this.detectUrlValue, { method: "POST", body })
      .then((response) => response.json().then((json) => ({ ok: response.ok, json })))
      .then(({ ok, json }) => {
        if (requestId !== this.detectionRequestId || targetFile !== this.targetFile) return

        if (!ok || json.status !== "ok") {
          this.showMessage(json.message || this.t("inspect_failed", "Could not inspect that image."), "error")
          this.applyDetectedFaces([], "")
          return
        }

        this.targetUploadToken = json.target_upload_token || null
        this.previewDimensions = { width: json.width, height: json.height }
        this.applyDetectedFaces(json.faces || [], json.detector || "preview")
      })
      .catch(() => {
        if (requestId !== this.detectionRequestId || targetFile !== this.targetFile) return

        this.showMessage(
          this.t("inspect_failed_retry", "Could not inspect that image. Please try another target image."),
          "error"
        )
        this.applyDetectedFaces([], "")
      })
  }

  applyDetectedFaces(faces, detectorLabel) {
    this.detectedFaces = faces
    this.detectorLabelTarget.textContent = this.tp("face_count", faces.length, "%{count} face", "%{count} faces")
    this.renderFaceBoxes()
    this.renderFaceOptions()

    if (faces.length === 1) {
      this.selectFaceIndex(faces[0].index)
    } else {
      this.updateSelectionSummary()
      this.updateRunState()
    }

    if (detectorLabel) {
      this.previewShellTarget.dataset.detector = detectorLabel
    }
  }

  renderFaceBoxes() {
    this.overlayTarget.innerHTML = ""
    if (!this.previewDimensions || this.detectedFaces.length === 0) return

    const previewFrame = this.previewFrame()
    if (!previewFrame) return

    this.detectedFaces.forEach((face) => {
      const box = document.createElement("button")
      box.type = "button"
      box.className = this.faceBoxClasses(face.index === this.targetFaceIndex && this.selectionMode === "index")
      box.style.left = `${previewFrame.left + (face.bbox[0] * previewFrame.scaleX)}px`
      box.style.top = `${previewFrame.top + (face.bbox[1] * previewFrame.scaleY)}px`
      box.style.width = `${(face.bbox[2] - face.bbox[0]) * previewFrame.scaleX}px`
      box.style.height = `${(face.bbox[3] - face.bbox[1]) * previewFrame.scaleY}px`
      box.textContent = String(face.index + 1)
      box.addEventListener("click", () => this.selectFaceIndex(face.index))
      this.overlayTarget.appendChild(box)
    })
  }

  previewFrame() {
    const displayedWidth = this.previewImageTarget.clientWidth
    const displayedHeight = this.previewImageTarget.clientHeight
    const sourceWidth = Number(this.previewDimensions?.width || 0)
    const sourceHeight = Number(this.previewDimensions?.height || 0)
    if (!displayedWidth || !displayedHeight || !sourceWidth || !sourceHeight) return null

    const scale = Math.min(displayedWidth / sourceWidth, displayedHeight / sourceHeight)
    const renderedWidth = sourceWidth * scale
    const renderedHeight = sourceHeight * scale

    return {
      left: this.previewImageTarget.offsetLeft + ((displayedWidth - renderedWidth) / 2),
      top: this.previewImageTarget.offsetTop + ((displayedHeight - renderedHeight) / 2),
      scaleX: scale,
      scaleY: scale
    }
  }

  faceBoxClasses(selected) {
    return [
      "pointer-events-auto",
      "absolute",
      "flex",
      "items-start",
      "justify-start",
      "rounded",
      "border-2",
      "px-1.5",
      "py-1",
      "text-xs",
      "font-bold",
      "shadow-lg",
      selected ? "border-blue-300 bg-blue-500/40 text-white" : "border-white bg-gray-950/30 text-white"
    ].join(" ")
  }

  renderFaceOptions() {
    this.faceOptionsTarget.innerHTML = ""
    this.detectedFaces.forEach((face) => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = this.faceChipClasses(face.index === this.targetFaceIndex && this.selectionMode === "index")
      button.textContent = this.t("face_option", "Face %{number}", { number: face.index + 1 })
      button.addEventListener("click", () => this.selectFaceIndex(face.index))
      this.faceOptionsTarget.appendChild(button)
    })
  }

  faceChipClasses(selected) {
    const base = "rounded-md border px-3 py-3 text-sm font-medium"
    return selected
      ? `${base} border-blue-300 bg-blue-600 text-white`
      : `${base} border-gray-700 bg-gray-900 text-gray-200 hover:border-blue-400`
  }

  selectAuto() {
    this.selectionMode = "auto"
    this.targetFaceIndex = null
    this.renderFaceBoxes()
    this.renderFaceOptions()
    this.updateSelectionSummary()
    this.updateRunState()
  }

  selectAll() {
    this.selectionMode = "all"
    this.targetFaceIndex = null
    this.renderFaceBoxes()
    this.renderFaceOptions()
    this.updateSelectionSummary()
    this.updateRunState()
  }

  selectFaceIndex(index) {
    const selectedIndex = Number(index)
    if (!this.detectedFaces.some((face) => Number(face.index) === selectedIndex)) return

    this.selectionMode = "index"
    this.targetFaceIndex = selectedIndex
    this.renderFaceBoxes()
    this.renderFaceOptions()
    this.updateSelectionSummary()
    this.updateRunState()
  }

  updateSelectionSummary() {
    let text = this.t("selection_auto", "Selection: Auto")
    if (this.selectionMode === "all") text = this.t("selection_all", "Selection: All faces")
    if (this.selectionMode === "index") {
      text = this.t("selection_face", "Selection: Face %{number}", { number: this.targetFaceIndex + 1 })
    }
    this.selectionSummaryTarget.textContent = text
  }

  updateRunState() {
    if (this.queued) {
      this.runButtonTarget.disabled = true
      return
    }

    const hasFace = Boolean(this.activeTask || this.selectedFaceId)
    const hasDetectedTarget = Boolean(this.targetFile && this.targetUploadToken && this.detectedFaces.length > 0)
    this.runButtonTarget.disabled = !(hasFace && hasDetectedTarget)
  }

  createTask() {
    if (this.demoValue && !this.initData) {
      this.showMessage(
        this.t("demo_complete", "Demo complete: the real mini app queues a task with this selection."),
        "success"
      )
      return
    }

    if (!this.initData) {
      this.showMessage(this.t("open_from_telegram_before_queueing", "Open this from Telegram before queueing."), "error")
      return
    }

    if (!this.targetFile || !this.targetUploadToken || !(this.activeTask || this.selectedFaceId)) return
    if (this.selectionMode === "index" && !this.detectedFaces.some((face) => Number(face.index) === this.targetFaceIndex)) {
      this.showMessage(
        this.t(
          "select_detected_face_before_queueing",
          "Select one of the detected target faces before queueing."
        ),
        "error"
      )
      return
    }

    this.runButtonTarget.disabled = true
    this.runButtonTarget.textContent = this.t("queueing", "Queueing...")

    const body = new FormData()
    body.append("init_data", this.initData)
    body.append("target_upload_token", this.targetUploadToken)
    if (this.taskToken) body.append("task_token", this.taskToken)
    if (this.selectedFaceId) body.append("selected_face_id", this.selectedFaceId)
    body.append("selection_mode", this.selectionMode)
    body.append("detected_faces_count", String(this.detectedFaces.length))
    if (this.selectionMode === "index") {
      body.append("target_face_index", String(this.targetFaceIndex))
    }

    fetch(this.createUrlValue, { method: "POST", body })
      .then((response) => response.json().then((json) => ({ ok: response.ok, json })))
      .then(({ ok, json }) => {
        if (!ok || json.status !== "ok") {
          this.showMessage(json.message || this.t("queue_failed", "Could not queue this swap."), "error")
          return
        }

        this.showMessage(json.message || this.t("queued", "Queued."), "success")
        this.queued = true
        this.runButtonTarget.textContent = this.t("queued_button", "Queued")
        this.runButtonTarget.disabled = true
        try { window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred("success") } catch (_) {}
        this.closeTelegramWebApp()
      })
      .catch(() => this.showMessage(this.t("queue_failed", "Could not queue this swap."), "error"))
      .finally(() => {
        if (this.queued) return

        this.runButtonTarget.textContent = this.t("queue_swap_button", "Queue swap")
        this.updateRunState()
      })
  }

  closeTelegramWebApp() {
    window.setTimeout(() => {
      try { window.Telegram?.WebApp?.close() } catch (_) {}
    }, 600)
  }

  localPreviewFaces() {
    const width = this.previewDimensions?.width || 1000
    const height = this.previewDimensions?.height || 750
    const size = Math.min(width, height) * 0.28
    const centers = width / height >= 1.15 ? [width * 0.32, width * 0.68] : [width * 0.5]
    const y = Math.min(height * 0.24, height - size)

    return centers.map((centerX, index) => {
      const x = Math.min(Math.max(centerX - size / 2, 0), width - size)
      return {
        index,
        label: `Face ${index + 1}`,
        bbox: [x, y, x + size, y + size],
        confidence: 0.9
      }
    })
  }

  formatBalance(value) {
    const number = Number(value || 0)
    return `$${number.toFixed(2)}`
  }

  t(path, fallback, values = {}) {
    const template = this.lookupCopy(path) || fallback
    return this.interpolate(template, values)
  }

  tp(path, count, fallbackOne, fallbackOther) {
    const candidates = this.lookupCopy(path) || {}
    const fallback = count === 1 ? fallbackOne : fallbackOther
    const template = count === 1 ? candidates.one : candidates.other
    return this.interpolate(template || fallback, { count })
  }

  lookupCopy(path) {
    if (!this.hasCopyValue) return null

    return path.split(".").reduce((value, part) => {
      if (!value || typeof value !== "object") return null
      return value[part]
    }, this.copyValue)
  }

  interpolate(template, values = {}) {
    return String(template).replace(/%\{([a-zA-Z0-9_]+)\}/g, (_, key) => values[key] ?? "")
  }

  showMessage(message, type) {
    if (type === "clear" || !message) {
      this.messageTarget.classList.add("hidden")
      this.messageTarget.textContent = ""
      return
    }

    this.messageTarget.className = this.messageClasses(type)
    this.messageTarget.textContent = message
  }

  messageClasses(type) {
    const base = "rounded-md border px-3 py-3 text-sm"
    if (type === "success") return `${base} border-green-500/40 bg-green-950/50 text-green-100`
    if (type === "info") return `${base} border-blue-500/40 bg-blue-950/50 text-blue-100`
    return `${base} border-red-500/40 bg-red-950/50 text-red-100`
  }

}
